import { FC, ReactNode, useId } from 'react';
import { IonPopover } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { BRAND, CHART_INK } from './agencyChartTheme';

const useStyles = createUseStyles({
    button: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        flexShrink: 0,
        padding: 0,
        borderRadius: '50%',
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'none',
        font: 'inherit',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        color: BRAND.deepTeal,
        cursor: 'pointer',

        '&:hover': {
            background: BRAND.tealWash,
            borderColor: BRAND.deepTeal
        }
    },

    popover: {
        '--max-width': '300px',
        '--width': '300px'
    },

    body: {
        padding: '12px 14px',
        fontSize: 12.5,
        lineHeight: 1.55,
        color: CHART_INK.strongLabel
    }
});

/**
 * The explanation of a diagram, tucked behind a mark beside its heading.
 *
 * It is a caveat, not a caption: everyone reads it once and nobody reads it again, so leaving it
 * printed under the chart costs every later visit a paragraph of scrolling. Behind a control it is
 * still one click away for the person who has not met the diagram before.
 *
 * A button rather than a hover target, so it works on a touch screen and can be reached by keyboard.
 */
export const InsightsInfoTip: FC<{ label: string; children: ReactNode }> = ({ label, children }) => {
    const classes = useStyles();
    const triggerId = `info-${useId()}`;

    return (
        <>
            <button
                id={triggerId}
                type="button"
                className={classes.button}
                data-id="insights-info-tip"
                aria-label={label}
            >
                ?
            </button>

            <IonPopover
                className={classes.popover}
                trigger={triggerId}
                triggerAction="click"
                side="bottom"
                alignment="end"
                showBackdrop={false}
                dismissOnSelect
            >
                <div className={classes.body}>{children}</div>
            </IonPopover>
        </>
    );
};
